/**
 * BETA INSIGHTS.
 *
 * Deliberately small. This is not an analytics suite and must not become one:
 * it renders the metrics `pilot-telemetry-1` already computes, each beside
 * the source the backend named for it, and it computes nothing of its own.
 *
 * Two things it will never show, because the payload contains no field from
 * which either could honestly be derived: a monetary figure, and a claim
 * about time or effort saved. `Average time to decide` is how long a person
 * took to answer a proposal — the opposite of a saving.
 *
 * The panel is internal-only. The endpoint carries the
 * `execution_intelligence` entitlement but NO role gate at all — it uses bare
 * `get_current_user` — so every authenticated member of an entitled tenant
 * can read the commercial funnel. Keeping that off an external provider's
 * screen is this component's job; the backend will not do it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { UnknownValue } from "../../components/workflow/WorkflowStates";
import {
  Activity,
  RefreshCw,
  Info,
  Loader2,
  ShieldOff,
  TrendingUp,
  Clock,
  HelpCircle,
} from "lucide-react";
import { executionIntelligenceApi } from "../../services/intelligenceService";
import { classifyWorkflowError } from "../../lib/workflowErrors";
import { createSequencer } from "../../lib/sequencer";
import { presentTelemetry, auditLoadCapped } from "./telemetryPresentation";

/**
 * One figure. A metric the backend could not establish renders as UNKNOWN,
 * never as a zero — the difference between "nothing happened" and "we cannot
 * tell" is the whole point of this surface.
 */
const Metric = ({ metric }) => (
  <div className="min-w-0 space-y-0.5">
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{metric.label}</p>
    {metric.known ? (
      <p className="text-lg font-semibold leading-none">
        {typeof metric.value === "number" ? metric.value.toLocaleString() : metric.value}
        {metric.unit && (
          <span className="text-[11px] font-normal text-muted-foreground ml-1">{metric.unit}</span>
        )}
      </p>
    ) : (
      <UnknownValue reason={metric.caveat || "The backend could not establish this."} />
    )}
    {metric.caveat && metric.known && (
      <p className="text-[10px] text-muted-foreground">{metric.caveat}</p>
    )}
  </div>
);

/** A family of metrics, with the source the backend named for all of them. */
const Family = ({ title, Icon, basis, children, footer }) => (
  <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2">
    <p className="text-xs font-semibold flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 text-primary" />
      {title}
    </p>
    {children}
    {footer}
    {/* Every count names its source. A count with no named source is a claim,
        not a measurement — so the basis is shown, not hidden behind a tooltip. */}
    {basis && (
      <details className="pt-1 border-t border-border/40">
        <summary className="text-[10px] text-muted-foreground cursor-pointer select-none">
          What this is measured from
        </summary>
        <p className="text-[10px] text-muted-foreground mt-1 break-words">{basis}</p>
      </details>
    )}
  </div>
);

const BetaInsightsPanel = ({ projectId }) => {
  const [telemetry, setTelemetry] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(createSequencer()).current;

  const load = useCallback(async () => {
    if (!projectId) {
      seq.invalidate();
      setTelemetry(null);
      return;
    }
    const ticket = seq.next();
    setLoading(true);
    setError(null);
    try {
      const data = await executionIntelligenceApi.pilotTelemetry(projectId);
      if (!seq.isCurrent(ticket)) return;
      // Tagged with its subject, so a late answer for another project can
      // never be rendered as this one's.
      setTelemetry({ projectId, data });
    } catch (err) {
      if (!seq.isCurrent(ticket)) return;
      setTelemetry(null);
      setError(classifyWorkflowError(err));
    } finally {
      if (seq.isCurrent(ticket)) setLoading(false);
    }
  }, [projectId, seq]);

  useEffect(() => {
    load();
  }, [load]);

  const presented = useMemo(
    () => (telemetry?.projectId === projectId ? presentTelemetry(telemetry.data) : null),
    [telemetry, projectId]
  );
  const countsAreFloors = useMemo(
    () => (telemetry?.projectId === projectId ? auditLoadCapped(telemetry.data) : false),
    [telemetry, projectId]
  );

  if (error) {
    const copy =
      error.kind === "not-entitled"
        ? "Your company has not been granted execution intelligence, which is where these measurements come from."
        : error.kind === "disabled"
          ? "Execution intelligence is turned off on this platform."
          : error.kind === "no-access"
            ? "Your role may not read this project's measurements."
            : error.message;
    /**
     * A refusal that is a property of the surface (no entitlement, flag off,
     * wrong role) will answer the same way next time, so retrying it is
     * noise. A network failure will not — and removing the only Refresh
     * control leaves a dead panel with no way back short of a page reload.
     */
    const worthRetrying = !["not-entitled", "disabled", "no-access"].includes(error.kind);
    return (
      <Card className="border-border" data-testid="beta-insights-panel">
        <CardContent className="py-4 space-y-2">
          <Alert className="border-border bg-muted/20">
            <ShieldOff className="h-4 w-4" />
            <AlertDescription className="text-xs">{copy}</AlertDescription>
          </Alert>
          {worthRetrying && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={load}
              disabled={loading}
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Try again
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border" data-testid="beta-insights-panel">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" />
            Beta insights
          </CardTitle>
          <div className="flex items-center gap-2">
            {presented?.dataDate && (
              <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground border-border">
                {/* dataDate is when the snapshot was taken — NOT the bound of
                    a window. There is no date filter in any query, so calling
                    this "this week" would be false. */}
                Everything so far, read {presented.dataDate}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={load}
              disabled={loading}
              // An icon carries no name to a screen reader.
              aria-label="Refresh these measurements"
              title="Refresh these measurements"
            >
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/*
          A contract version this client was not written against means every
          figure below may be read from a key that has moved. Saying nothing
          would render confident zeroes over a shape nobody has checked.
        */}
        {presented && !presented.contractMatches && (
          <Alert className="border-amber-500/40 bg-amber-500/10">
            <Info className="h-4 w-4" />
            <AlertDescription className="text-[11px]">
              These measurements arrived as <span className="font-mono">{presented.contractVersion || "an unnamed version"}</span>, which this
              screen was not written against. Treat every figure below as unconfirmed.
            </AlertDescription>
          </Alert>
        )}

        {/*
          The audit history is capped at 10,000 rows and the backend reports
          that in prose rather than a flag. When it bites, every
          audit-derived count is a floor — which is most of this panel.
        */}
        {countsAreFloors && (
          <Alert className="border-amber-500/40 bg-amber-500/10">
            <Info className="h-4 w-4" />
            <AlertDescription className="text-[11px]">
              This project has more history than one read can cover, so the counts below are
              minimums rather than totals.
            </AlertDescription>
          </Alert>
        )}

        {loading && !presented && (
          <p className="text-xs text-muted-foreground">Reading this project's measurements…</p>
        )}

        {presented && (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              {presented.blockers && (
                <Family title="Blockers" Icon={ShieldOff} basis={presented.blockers.basis}>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {presented.blockers.metrics.map((m) => (
                      <Metric key={m.label} metric={m} />
                    ))}
                  </div>
                  {presented.blockers.detectionsTruncated && !countsAreFloors && (
                    <p className="text-[10px] text-muted-foreground">
                      More detections happened than are counted in the detail the backend returns.
                    </p>
                  )}
                </Family>
              )}

              {presented.unknowns && (
                <Family title="Unknowns" Icon={HelpCircle} basis={presented.unknowns.basis}>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {presented.unknowns.metrics.map((m) => (
                      <Metric key={m.label} metric={m} />
                    ))}
                  </div>
                  <div className="pt-1">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Still unresolved
                    </p>
                    {presented.unknowns.ratePercent === null ? (
                      <UnknownValue reason={presented.unknowns.rateUnknownBecause || undefined} />
                    ) : (
                      <p className="text-lg font-semibold leading-none">
                        {presented.unknowns.ratePercent}%
                      </p>
                    )}
                    {presented.unknowns.denominator && (
                      <p className="text-[10px] text-muted-foreground">
                        Out of {presented.unknowns.denominator}
                      </p>
                    )}
                  </div>
                </Family>
              )}

              {presented.decisions && (
                <Family title="Decisions" Icon={Clock} basis={presented.decisions.basis}>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {presented.decisions.metrics.map((m) => (
                      <Metric key={m.label} metric={m} />
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Recovery proposals: {presented.decisions.recoveryAcceptance.approved} approved ·{" "}
                    {presented.decisions.recoveryAcceptance.rejected} rejected. Schedule proposals
                    only — award decisions are counted under commercial progress.
                  </p>
                </Family>
              )}

              {presented.scheduleImpact && (
                <Family
                  title="Schedule impact"
                  Icon={TrendingUp}
                  basis={presented.scheduleImpact.basis}
                >
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {presented.scheduleImpact.metrics.map((m) => (
                      <Metric key={m.label} metric={m} />
                    ))}
                  </div>
                </Family>
              )}
            </div>

            {presented.commercial && (
              <Family
                title="Commercial progress"
                Icon={TrendingUp}
                basis={presented.commercial.basis}
              >
                <div className="grid gap-x-4 gap-y-2 [grid-template-columns:repeat(auto-fill,minmax(8rem,1fr))]">
                  {presented.commercial.funnel.map((stage) => (
                    <div key={stage.label} className="min-w-0 space-y-0.5">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {stage.label}
                      </p>
                      <p className="text-lg font-semibold leading-none">
                        {stage.value.toLocaleString()}
                      </p>
                      {stage.caveat && (
                        <p className="text-[10px] text-muted-foreground">{stage.caveat}</p>
                      )}
                    </div>
                  ))}
                </div>
              </Family>
            )}

            {presented.readiness && (
              <Family title="Recalculations" Icon={RefreshCw} basis={presented.readiness.basis}>
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {presented.readiness.actions.map((action) => (
                    <div key={action.action} className="min-w-0">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {action.label}
                      </p>
                      <p className="text-sm font-semibold">{action.count.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </Family>
            )}

            {/* The honest-gaps channel. It is never empty on a successful
                read, so its presence is not news — its contents are. */}
            {presented.missingInformation.length > 0 && (
              <Alert className="border-border bg-muted/20">
                <Info className="h-4 w-4" />
                <AlertDescription className="space-y-1">
                  <p className="text-[11px] font-medium">What these numbers do not cover</p>
                  {presented.missingInformation.map((note, index) => (
                    <p key={index} className="text-[10px] text-muted-foreground">
                      {note}
                    </p>
                  ))}
                </AlertDescription>
              </Alert>
            )}

            <p className="text-[10px] text-muted-foreground border-t border-border/50 pt-2">
              Every figure here is a count OMNΛS already keeps, measured from the source named under
              it. Nothing on this panel is a saving, a return, or an estimate of effort avoided —
              the platform records no cost or effort data from which such a figure could be
              calculated.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BetaInsightsPanel;
